import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { staffApi, STATUS_META, money, timeSince } from "./staffApi";
import { playTone, doVibrate, inQuietHours } from "./alerts";
import { usePinPrompt } from "./PinModal";

const VIEWS = [
  { key: "open", label: "OPEN ORDERS" },
  { key: "mine", label: "MY TABLES" },
  { key: "all", label: "ALL TABLES" },
  { key: "attention", label: "NEEDS ATTENTION" },
  { key: "done", label: "DONE" },
];

const ACTIVE = (o) => !["completed", "cancelled"].includes(o.status);
const secsOld = (iso) => Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);

export const Board = ({ person, onOpen, onGoHome }) => {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("open");
  const [showFailed, setShowFailed] = useState(false);
  const [hideMin, setHideMin] = useState(30);
  const [repeatSec, setRepeatSec] = useState(30);
  const [esc, setEsc] = useState({ red_seconds: 30, manager_seconds: 60, inactive_warn_minutes: 90 });
  const [unackedCount, setUnackedCount] = useState(0);
  const [askPin, pinEl] = usePinPrompt();
  const known = useRef(null);
  const knownCalls = useRef(null);
  const prefsRef = useRef({ sound_on: true, sound_id: 3, vibrate_on: false });
  const cfgRef = useRef({ repeat_seconds: 30, quiet_start: "23:00", quiet_end: "10:00" });
  const attentionRef = useRef([]);
  const acked = useRef(new Set());

  const fireAlert = () => {
    const cfg = cfgRef.current;
    if (inQuietHours(cfg.quiet_start, cfg.quiet_end)) return;
    const p = prefsRef.current;
    if (p.sound_on) playTone(p.sound_id);
    if (p.vibrate_on) doVibrate();
  };

  useEffect(() => {
    if (!person) return undefined;
    const sync = () => staffApi.get("/staff/me/alert-prefs").then((r) => { prefsRef.current = r.data; }).catch(() => {});
    sync();
    window.addEventListener("focus", sync);
    return () => window.removeEventListener("focus", sync);
  }, [person]);

  useEffect(() => {
    const t = setInterval(() => {
      if (attentionRef.current.some((id) => !acked.current.has(id))) fireAlert();
    }, repeatSec * 1000);
    return () => clearInterval(t);
  }, [repeatSec]);

  useEffect(() => {
    const load = () => staffApi.get("/staff/board").then((r) => {
      const list = r.data.orders;
      const openCalls = r.data.calls || [];
      const waiting = list.filter((o) => o.status === "waiting_for_waiter").map((o) => o.order_id)
        .concat(list.filter((o) => o.new_round_pending).map((o) => `${o.order_id}:r${o.rounds_count}`))
        .concat(list.filter((o) => o.status === "ready").map((o) => `${o.order_id}:ready`));
      const newOrder = known.current !== null && waiting.some((id) => !known.current.has(id));
      const newCall = knownCalls.current !== null && openCalls.some((c) => !knownCalls.current.has(c.call_id));
      if (newOrder || newCall) fireAlert();
      known.current = new Set(waiting);
      knownCalls.current = new Set(openCalls.map((c) => c.call_id));
      attentionRef.current = waiting.concat(openCalls.map((c) => `call:${c.call_id}`));
      setUnackedCount(attentionRef.current.filter((id) => !acked.current.has(id)).length);
      setData(r.data);
      if (r.data.board_done_hide_minutes) setHideMin(r.data.board_done_hide_minutes);
      if (r.data.escalation) setEsc(r.data.escalation);
      if (r.data.alert_config) { cfgRef.current = r.data.alert_config; setRepeatSec(r.data.alert_config.repeat_seconds || 30); }
    }).catch(() => {});
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, []);

  if (!data) return <p className="p-8 text-white/50 text-lg">Loading the floor…</p>;

  const { orders, calls, untagged_lists: untagged, pos_failed: posFailed, closed_orders: closed, attention, on_shift: onShift } = data;
  const posAlarm = data.pos_alarm;

  const accept = async (o) => {
    const url = o.status === "waiting_for_waiter" ? "accept" : "claim";
    const post = (pin) => staffApi.post(`/staff/orders/${o.order_id}/${url}`, pin ? { pin } : {});
    try {
      if (person) await post(null);
      else {
        const pin = await askPin(`Accept Table ${o.table}`, "Your PIN puts your name on this whole visit");
        if (!pin) return;
        await post(pin);
      }
      toast.success(`Table ${o.table} is yours — every order from this party comes to you`);
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = typeof d === "string" ? d : "Could not accept";
      if (msg.startsWith("Start your shift")) toast.error(msg, { action: { label: "GO TO STAFF", onClick: onGoHome } });
      else toast.error(msg);
    }
  };

  const ack = (id) => staffApi.post(`/staff/calls/${id}/ack`).then(() => setData((d) => ({ ...d, calls: d.calls.filter((c) => c.call_id !== id) }))).catch(() => {});
  const resolveFailed = (jobId) =>
    staffApi.post(`/staff/pos-sync/${jobId}/resolve`)
      .then(() => setData((d) => ({ ...d, pos_failed: d.pos_failed.filter((f) => f.job_id !== jobId) })))
      .catch(() => toast.error("Sign in to resolve sync — it's an accountable action"));
  const setting = (body) => staffApi.post("/staff/board-settings", body).catch(() => toast.error("Manager or owner only"));

  const shown = filter === "done" ? closed
    : filter === "open" ? orders.filter((o) => ACTIVE(o) && !o.assigned_staff_id)
    : filter === "mine" ? (person ? orders.filter((o) => ACTIVE(o) && o.assigned_staff_id === person.staff_id) : [])
    : filter === "all" ? orders.filter(ACTIVE)
    : [];
  const openCount = orders.filter((o) => ACTIVE(o) && !o.assigned_staff_id).length;
  const isMgr = person && (person.role === "manager" || person.role === "owner");

  return (
    <div data-testid="staff-board" className="p-4 pb-16 max-w-6xl mx-auto">
      {!person && (
        <p data-testid="board-viewer-note" className="mb-3 rounded-xl border border-white/10 bg-white/4 text-white/55 text-sm font-bold px-4 py-3">
          View the Floor Board. Enter your PIN when accepting or managing an order.
        </p>
      )}
      {calls.map((c) => {
        const owner = orders.find((o) => o.table === c.table && ACTIVE(o));
        return (
          <div key={c.call_id} data-testid={`call-banner-${c.call_id}`}
            className="glow-amber w-full mb-3 rounded-2xl bg-[#F59E0B]/12 p-4 flex items-center justify-between gap-3">
            <div>
              <p className="text-xl font-black text-[#F59E0B]">🖐 TABLE {c.table} IS CALLING{owner?.assigned_name ? ` — ${owner.assigned_name.toUpperCase()}'S TABLE` : ""}</p>
              <p className="text-sm text-white/60 font-bold mt-0.5">{timeSince(c.created_at)}{c.reason ? ` · “${c.reason}”` : ""}</p>
            </div>
            <button data-testid={`call-ack-${c.call_id}`} onClick={() => ack(c.call_id)}
              className="shrink-0 rounded-xl bg-[#F59E0B] text-black font-black px-5 py-3.5 text-sm tracking-wide">ON MY WAY</button>
          </div>
        );
      })}
      {openCount > 0 && filter !== "open" && (
        <button data-testid="board-new-banner" onClick={() => setFilter("open")}
          className="w-full mb-4 rounded-2xl bg-[#FF5A4E] text-black font-black text-xl py-4 tracking-wide animate-pulse">
          🔔 {openCount} OPEN ORDER{openCount > 1 ? "S" : ""} WAITING — TAP TO VIEW
        </button>
      )}
      {unackedCount > 0 && (
        <button data-testid="alert-ack-chip" onClick={() => { acked.current = new Set(attentionRef.current); setUnackedCount(0); }}
          className="w-full mb-4 rounded-2xl border-2 border-[#F0B429] bg-[#F0B429]/15 p-3 text-left">
          <span className="text-[#F0B429] font-black text-base tracking-wide">🔔 {unackedCount} NEED{unackedCount === 1 ? "S" : ""} ATTENTION — TAP TO SILENCE REPEATS</span>
          <span className="block text-xs text-white/50 font-bold mt-0.5">Alerts repeat every {repeatSec}s until acknowledged, so nothing sits cold under the pass.</span>
        </button>
      )}
      {posAlarm?.alarm && (
        <div data-testid="board-pos-alarm-banner" className="w-full mb-4 rounded-2xl border-2 border-[#FF5A4E] bg-[#FF5A4E]/15">
          <button data-testid="board-pos-alarm-toggle" onClick={() => setShowFailed((v) => !v)} className="w-full text-left p-4">
            <p className="text-[#FF5A4E] font-black text-lg tracking-wide">
              ⛔ POS SYNC ALARM — {[posAlarm.failed > 0 && `${posAlarm.failed} FAILED`,
                posAlarm.stale_pending > 0 && `${posAlarm.stale_pending} PENDING OVER AN HOUR`].filter(Boolean).join(" · ")}
            </p>
            <p className="text-sm text-white/60 font-bold mt-0.5">{showFailed ? "Resolve each one below, or tap a row to open the full order." : "Tap to see exactly which orders — resolve them right here."}</p>
          </button>
          {showFailed && (
            <div data-testid="board-pos-failed-list" className="px-4 pb-4 space-y-2">
              {posFailed.length === 0 && <p className="text-sm text-white/50 font-bold">Nothing failed — only stale pending jobs remain. They retry on their own.</p>}
              {posFailed.map((f) => (
                <div key={f.job_id} data-testid={`pos-failed-row-${f.job_id}`} className="rounded-xl bg-black/40 border border-white/10 p-3 flex items-center gap-3">
                  <button onClick={() => onOpen(f.order_id)} className="flex-1 min-w-0 text-left">
                    <p className="font-black text-base">T{f.table || "?"} · {f.order_id}
                      <span className="text-white/40 font-bold text-xs"> · {timeSince(f.failed_at)}{f.error_code ? ` · ${f.error_code}` : ""}</span>
                    </p>
                    <p className="text-sm text-white/60 truncate">{(f.items || []).map((i) => `${i.qty}× ${i.name}`).join(" · ") || "—"}</p>
                  </button>
                  <button data-testid={`pos-failed-resolve-${f.job_id}`} onClick={() => resolveFailed(f.job_id)}
                    className="shrink-0 rounded-lg border border-[#FF5A4E] text-[#FF5A4E] px-3 py-2 text-xs font-black">ENTERED IN POS MANUALLY</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-1 -mx-4 px-4">
        {VIEWS.map((f) => (
          <button key={f.key} data-testid={`board-filter-${f.key}`} onClick={() => setFilter(f.key)}
            className={`shrink-0 rounded-full px-5 py-3 text-sm font-black tracking-wide border ${
              filter === f.key ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "s-card text-white/65 hover:border-[#D4AF37]/40"}`}>
            {f.label}{f.key === "open" && openCount > 0 ? ` · ${openCount}` : ""}{f.key === "attention" && attention.length > 0 ? ` · ${attention.length}` : ""}
          </button>
        ))}
      </div>

      {filter === "attention" && (
        <div data-testid="board-attention" className="mt-4 space-y-2 max-w-3xl">
          {attention.length === 0 && <p data-testid="attention-clear" className="text-white/40 text-lg mt-8 text-center">All clear — nothing needs a person.</p>}
          {attention.map((a, i) => (
            <button key={i} data-testid={`attention-row-${a.order_id || a.kind}`}
              onClick={() => a.order_id && onOpen(a.order_id)}
              className="w-full text-left rounded-2xl border-2 border-[#F0B429]/60 bg-[#F0B429]/8 p-4 hover:border-[#F0B429]">
              <p className="font-black text-base text-[#F0B429]">{a.table ? `TABLE ${a.table} · ${a.order_id}` : "SYNC"}</p>
              <p className="text-sm text-white/70 font-bold mt-0.5">{a.detail}</p>
            </button>
          ))}
        </div>
      )}

      {filter === "mine" && !person && (
        <div data-testid="mine-needs-person" className="mt-8 text-center">
          <p className="text-white/55 text-lg font-bold">My Tables is per-person.</p>
          <button data-testid="mine-go-home" onClick={onGoHome} className="mt-3 rounded-xl bg-[#D4AF37] text-black font-black px-6 py-3.5">GO TO STAFF — TAP YOUR TILE</button>
        </div>
      )}

      {filter === "done" && isMgr && (
        <div data-testid="board-hide-min-control" className="mt-3 flex items-center gap-2 flex-wrap text-sm font-bold text-white/60">
          <span>DONE tickets leave the live board after:</span>
          {[10, 30, 60, 120, 240].map((v) => (
            <button key={v} data-testid={`board-hide-min-${v}`} onClick={() => { setHideMin(v); setting({ done_hide_minutes: v }); }}
              className={`rounded-full px-3 py-1.5 text-xs font-black border ${hideMin === v ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/20 text-white/60"}`}>
              {v >= 60 ? `${v / 60}h` : `${v}m`}
            </button>
          ))}
          <span className="w-full" />
          <span>Alert repeat:</span>
          {[15, 30, 60, 120].map((v) => (
            <button key={v} data-testid={`alert-repeat-${v}`} onClick={() => { setRepeatSec(v); setting({ alert_repeat_seconds: v }); }}
              className={`rounded-full px-3 py-1.5 text-xs font-black border ${repeatSec === v ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/20 text-white/60"}`}>
              {v}s
            </button>
          ))}
          <span className="w-full" />
          <span>Unaccepted order turns red after:</span>
          {[20, 30, 45, 60].map((v) => (
            <button key={v} data-testid={`esc-red-${v}`} onClick={() => { setEsc({ ...esc, red_seconds: v }); setting({ escalate_red_seconds: v }); }}
              className={`rounded-full px-3 py-1.5 text-xs font-black border ${esc.red_seconds === v ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/20 text-white/60"}`}>
              {v}s
            </button>
          ))}
          <span>Manager alert after:</span>
          {[60, 90, 120, 180].map((v) => (
            <button key={v} data-testid={`esc-mgr-${v}`} onClick={() => { setEsc({ ...esc, manager_seconds: v }); setting({ escalate_manager_seconds: v }); }}
              className={`rounded-full px-3 py-1.5 text-xs font-black border ${esc.manager_seconds === v ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/20 text-white/60"}`}>
              {v}s
            </button>
          ))}
          <span className="ml-2">Quiet hours (no sound):</span>
          <input data-testid="quiet-start-input" defaultValue={cfgRef.current.quiet_start} onBlur={(e) => setting({ quiet_start: e.target.value })}
            className="w-20 bg-[#1A1A20] border border-white/15 rounded-lg px-2 py-1.5 text-xs text-center" placeholder="23:00" />
          <span>→</span>
          <input data-testid="quiet-end-input" defaultValue={cfgRef.current.quiet_end} onBlur={(e) => setting({ quiet_end: e.target.value })}
            className="w-20 bg-[#1A1A20] border border-white/15 rounded-lg px-2 py-1.5 text-xs text-center" placeholder="10:00" />
        </div>
      )}

      {filter !== "attention" && !(filter === "mine" && !person) && shown.length === 0 && (
        <p data-testid="board-empty" className="text-white/40 text-lg mt-10 text-center">
          {filter === "open" ? "No open orders — everything has a name on it." : "No tables here right now."}
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
        {filter !== "attention" && shown.map((o) => {
          const meta = STATUS_META[o.status] || STATUS_META.completed;
          const isNew = o.status === "waiting_for_waiter";
          const age = secsOld(o.created_at);
          const escalated = isNew && !o.assigned_staff_id && age >= esc.red_seconds;
          return (
            <div key={o.order_id} data-testid={`table-card-${o.order_id}`} role="button" tabIndex={0}
              onClick={() => onOpen(o.order_id)}
              className={`s-card text-left rounded-2xl p-4 cursor-pointer hover:-translate-y-0.5 ${isNew ? "flash-new" : ""}`}
              style={{ borderColor: escalated ? "#FF5A4E" : `${meta.color}45`, borderWidth: escalated ? 2 : undefined, background: isNew ? meta.bg : undefined }}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="num text-4xl font-bold tracking-tighter leading-none">T{o.table}</p>
                  <p className="text-[11px] text-white/45 font-bold mt-1.5">
                    {isNew ? `waiting ${Math.floor(age / 60)}m ${Math.floor(age % 60)}s` : timeSince(o.updated_at)}{o.guests ? ` · ${o.guests} guests` : ""}
                  </p>
                </div>
                <span className="rounded-full px-3 py-1.5 text-[11px] font-black tracking-wider shrink-0"
                  style={{ color: meta.color, background: meta.bg }}>{meta.label}</span>
              </div>
              {o.followup_of && (
                <p data-testid={`card-followup-${o.order_id}`} className="mt-2 text-[11px] font-black text-[#F0B429] tracking-wider">↩ SAME TABLE — NEW VISIT · after {o.followup_of}</p>
              )}
              {o.allergies && (
                <p data-testid={`card-allergy-${o.order_id}`} className="mt-2.5 flex items-center gap-1.5 text-[#FF5A4E] text-sm font-black">
                  <AlertTriangle size={15} /> ALLERGY: {o.allergies.slice(0, 40)}{o.allergies.length > 40 ? "…" : ""}
                </p>
              )}
              {o.new_round_pending && (
                <p data-testid={`card-round-${o.order_id}`} className="mt-2 text-[11px] font-black text-[#F0B429] tracking-wider">🔔 ROUND {o.rounds_count} TO CONFIRM — NEW ITEMS</p>
              )}
              {o.transfer_pending && (
                <p data-testid={`card-transfer-${o.order_id}`} className="mt-2 text-[11px] font-black text-[#5AB0FF] tracking-wider">⇄ PASS TO {String(o.transfer_pending.to_name || "").toUpperCase()} — AWAITING THEIR PIN</p>
              )}
              {o.pos_sync && !["sent", "manual"].includes(o.pos_sync.state) && o.status !== "pos_failed" && !["waiting_for_waiter", "waiter_reviewing"].includes(o.status) && (
                o.pos_sync.state === "failed" ? (
                  <p data-testid={`card-pos-failed-${o.order_id}`} className="mt-2 text-[11px] font-black text-[#FF5A4E] tracking-wider">⛔ SYNC FAILED — ENTER IN POS MANUALLY</p>
                ) : (
                  <p data-testid={`card-pos-stuck-${o.order_id}`} className="mt-2 text-[11px] font-black text-[#F59E0B] tracking-wider">⚠ NOT IN KITCHEN — POS SYNC PENDING</p>
                )
              )}
              {o.status === "pos_failed" && (
                <button data-testid={`card-pos-retry-${o.order_id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    staffApi.post(`/staff/pos-sync/${o.order_id}/retry`)
                      .then((r) => {
                        if (r.data.state === "sent") toast.success("In the kitchen now ✓");
                        else toast.error("POS still not answering — it keeps retrying on its own");
                      })
                      .catch(() => toast.error("Retry failed — try again"));
                  }}
                  className="mt-2 w-full min-h-[48px] rounded-xl bg-[#FF5A4E] text-white font-black text-sm tracking-wider hover:bg-[#FF7264]">
                  ⛔ NOT IN KITCHEN — RETRY
                </button>
              )}
              {o.status === "pos_failed" && o.pos_last_reply && (
                <p data-testid={`card-pos-reason-${o.order_id}`} className="mt-1.5 text-[11px] font-bold text-[#FF8A80] break-words">
                  POS said: {o.pos_last_reply.http_status ? `HTTP ${o.pos_last_reply.http_status} · ` : ""}{o.pos_last_reply.body}
                </p>
              )}
              {ACTIVE(o) && (
                <button data-testid={`card-change-table-${o.order_id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    const nt = window.prompt(`Move ${o.order_id} to which table? (now ${o.table})`);
                    if (!nt || !nt.trim()) return;
                    if (!window.confirm(`Move table ${o.table} → ${nt.trim()}? The POS ticket updates right away.`)) return;
                    staffApi.post(`/staff/orders/${o.order_id}/change-table`, { table: nt.trim() })
                      .then((r) => toast.success(`Now table ${r.data.table}${r.data.pos === "sent" ? " — POS ticket updated ✓" : r.data.pos === "not_sent_yet" ? "" : " — POS update queued"}`))
                      .catch((er) => toast.error(typeof er.response?.data?.detail === "string" ? er.response.data.detail : "Could not change table"));
                  }}
                  className="mt-2 w-full min-h-[44px] rounded-xl border border-white/25 text-white/80 font-black text-xs tracking-wider">
                  CHANGE TABLE
                </button>
              )}
              {ACTIVE(o) && !o.assigned_staff_id && (
                <button data-testid={`card-accept-${o.order_id}`}
                  onClick={(e) => { e.stopPropagation(); accept(o); }}
                  className="mt-3 w-full min-h-[54px] rounded-xl bg-[#D4AF37] text-black font-black text-base tracking-wide hover:bg-[#F0C745]">
                  ACCEPT ORDER
                </button>
              )}
              <div className="flex items-center justify-between mt-3 text-sm">
                <span className="text-white/55 font-semibold truncate">{o.assigned_name ? `👤 ${o.assigned_name}` : "Unassigned"}</span>
                <span className="font-black text-white/45">
                  {o.pos_total != null ? `${money(o.pos_total)} · till price` : ""}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      {untagged.length > 0 && filter !== "attention" && (
        <div data-testid="board-untagged" className="mt-6 rounded-2xl border-2 border-[#F59E0B]/70 bg-[#1A1305] p-4">
          <p className="text-lg font-black text-[#F59E0B]">⚠ NO TABLE — {untagged.length} ORDER LIST{untagged.length > 1 ? "S" : ""} FROM UNTAGGED DEVICES</p>
          <p className="text-sm text-white/55 font-bold mt-1">A guest built an order list without a table number. Find the guest or check the floor.</p>
          <div className="divide-y divide-white/10 mt-2">
            {untagged.map((u) => (
              <div key={u.user_id} data-testid={`untagged-list-${u.user_id}`} className="py-3">
                <p className="text-sm font-bold truncate">{u.items.map((i) => `${i.qty}× ${i.name}`).join(", ")}</p>
                <p className="text-[11px] text-white/45 font-bold mt-0.5">{timeSince(u.updated_at)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {onShift?.length > 0 && (
        <p data-testid="board-onshift-strip" className="mt-6 text-xs text-white/40 font-bold">
          ON SHIFT: {onShift.map((r) => r.name).join(" · ")}
        </p>
      )}
      {pinEl}
    </div>
  );
};
